import { useEffect, useState } from "react";

import { AssigneeType } from "@accurx/api/patient-messaging";
import { useCurrentUser, useCurrentWorkspace } from "@accurx/auth";
import { useCreateTicketWithNoteMutation } from "@accurx/concierge/hooks/mutations/useCreateTicketWithNote";
import { Ds, Feedback } from "@accurx/design";
import { PatientDemographics } from "@accurx/native";
import { ROUTES_INBOX } from "domains/inbox/routes";
import { generatePath, useHistory, useLocation } from "react-router";
import { toast } from "react-toastify";

import { NoteForm } from "../../../NoteForm/NoteForm";

export const Form = () => {
    const history = useHistory();
    const workspaceId = useCurrentWorkspace().orgId;
    const { user: currentUser } = useCurrentUser();
    const location = useLocation<{ patient?: PatientDemographics }>();
    const { patient } = location.state;

    const [conversationId, setConversationId] = useState("");
    const [note, setNote] = useState("");

    const { mutate, isLoading } = useCreateTicketWithNoteMutation({
        onError: () => {
            toast(<ErrorFeedback />);
        },
    });

    const handleSubmit = (note: string) => {
        if (!patient) {
            toast(<ErrorFeedback />);
            return;
        }

        const { patientExternalIdentityDto } = patient;
        const patientIds = patientExternalIdentityDto.patientExternalIds;
        const currentUserId = currentUser.accuRxUserId;
        const assignee = { userId: currentUserId, type: AssigneeType.User };

        mutate(
            { note, patientIds, assignee },
            {
                onSuccess: (data) => {
                    const { id, type } = data.identity;
                    setConversationId(`t-${type}-${id}`);
                },
            },
        );
    };

    useEffect(() => {
        if (!conversationId) return;

        const pathname = generatePath(ROUTES_INBOX.PatientAddNoteConversation, {
            workspaceId,
        });
        history.push({
            pathname,
            search: `conversationId=${conversationId}`,
            state: { patient },
        });
    }, [conversationId, history, workspaceId, patient]);

    return (
        <NoteForm
            value={note}
            onChange={setNote}
            onSubmit={handleSubmit}
            isLoading={isLoading}
        />
    );
};

const ErrorFeedback = () => {
    return (
        <Feedback
            colour="error"
            title={
                <Ds.Flex
                    as="span"
                    alignItems="flex-start"
                    justifyContent="space-between"
                >
                    <Ds.Text weight="bold" as="span">
                        An error occurred when trying to send your message
                    </Ds.Text>
                    <Ds.Button appearance="tertiary" title="Close alert">
                        <Ds.Button.Icon name="Cross" aria-label="Close alert" />
                    </Ds.Button>
                </Ds.Flex>
            }
        >
            <Ds.Flex flexDirection="column">
                <Ds.Text>
                    Go back to your message and try saving your note again. If
                    this keeps happening, please get in touch with our support
                    team.
                </Ds.Text>
            </Ds.Flex>
        </Feedback>
    );
};
